import { createContext, useContext, useEffect, useRef, useState } from "react";

// Lets a page show its title (and an optional back button) in the top header
// instead of rendering its own heading.

const PageTitleContext = createContext(null);
const CurrentPageContext = createContext(null);

let nextId = 0;

export function PageTitleProvider({ children }) {
  // Every mounted usePageTitle registers an entry; the newest one is shown.
  const [entries, setEntries] = useState([]);

  const value = useRef({
    upsert: (entry) =>
      setEntries((prev) => {
        const rest = prev.filter((e) => e.id !== entry.id);
        return [...rest, entry];
      }),
    remove: (id) => setEntries((prev) => prev.filter((e) => e.id !== id)),
  }).current;

  const page = entries.length ? entries[entries.length - 1] : null;

  return (
    <PageTitleContext.Provider value={value}>
      <CurrentPageContext.Provider value={page}>
        {children}
      </CurrentPageContext.Provider>
    </PageTitleContext.Provider>
  );
}

export function useCurrentPageTitle() {
  return useContext(CurrentPageContext);
}

/**
 * usePageTitle("Edit User", { back: "/users" })
 * back: a route to go to, or true to go to the previous page.
 */
export default function usePageTitle(title, { back } = {}) {
  const ctx = useContext(PageTitleContext);
  const idRef = useRef(null);
  if (idRef.current === null) idRef.current = ++nextId;

  useEffect(() => {
    if (!ctx) return undefined;
    ctx.upsert({ id: idRef.current, title, back });
    return undefined;
  }, [ctx, title, back]);

  useEffect(() => {
    if (!ctx) return undefined;
    const id = idRef.current;
    return () => ctx.remove(id);
  }, [ctx]);
}
